import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent, Card as UICard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  PlusIcon,
  PencilSimpleIcon,
  TrashIcon,
  CurrencyDollarIcon,
  CircleNotchIcon,
  WarningCircleIcon,
  FloppyDiskIcon,
  XIcon,
  ArrowCounterClockwiseIcon,
  WrenchIcon,
} from '@phosphor-icons/react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import ManualAmountModal from 'src/components/rebates/ManualAmountModal';
import { ProductGroup } from 'src/api/rebates/useProductGroups';
import { useReorderRebateRules } from 'src/api/rebates/useRebateRules';
import { RuleCardDisplay } from './RuleCardDisplay';
import { CoreEnumOptionAssociation } from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';
import { CoreEnumOptionDTO } from 'src/api/core/core-enums/useCoreEnums';
import { RuleCardEditable } from './RuleCardEditable';
import {
  useDeleteManualAmount,
  ManualAmount,
} from 'src/api/rebates/useManualAmounts';
import { useBulkDeleteManualAmounts } from 'src/api/rebates/useBulkDeleteManualAmounts';
import type { RebateLineItem } from 'src/api/rebates/useRebateLineItems';
import { useToast } from 'src/context/ToastContext';
import {
  getStatusStyle,
  getStatusLabel,
  type ChangeStatus,
} from 'src/components/common/ChangeStatus';
import {
  DivisionSimpleDTO,
  RebateRule,
  RebateRuleProductGroup,
} from '@erp/shared-types';

/**
 * Parses a "YYYY-MM-DD" date string into a local-midnight Date.
 * Using new Date("YYYY-MM-DD") interprets the string as UTC midnight,
 * which causes off-by-one day issues in non-UTC timezones.
 */
function parseLocalDate(dateStr: string): Date {
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * RebateRulesTab - Presentation Component
 *
 * This component is purely presentational - it renders UI and handles user interactions.
 * All business logic, state management, and API calls are handled by RebateRulesContainer.
 *
 * This follows the Container/Presenter pattern for clean separation of concerns.
 */
const SortableRuleCard = ({
  rule,
  expandedRules,
  onToggleRuleExpansion,
  divisions,
  agreementStatus,
  ruleValidationData,
  areProductGroupsSame,
  tagsByRuleId,
  availableTags,
  tagsLoadingProp,
  onRuleTagAdd,
  onRuleTagRemove,
  tagsUpdatingByRuleId,
  ManualAmountsSection,
  manualAmountsByRuleId,
  handleOpenManualAmountModal,
  handleDeleteManualAmount,
  handleBulkDeleteManualAmounts,
  isBulkDeleting,
  rebateTotalsByRule,
  tierStatusByRule,
  volumesByRule,
  agreementStartDate,
  agreementEndDate,
}: {
  rule: RebateRule;
  expandedRules: Set<string>;
  onToggleRuleExpansion: (ruleId: string) => void;
  divisions: DivisionSimpleDTO[];
  agreementStatus: 'draft' | 'finalized';
  ruleValidationData: Map<
    string,
    { hasErrors: boolean; validationErrors: string[] }
  >;
  areProductGroupsSame: (rule: RebateRule) => boolean;
  tagsByRuleId: Map<string, CoreEnumOptionAssociation[]>;
  availableTags: CoreEnumOptionDTO[];
  tagsLoadingProp: boolean;
  onRuleTagAdd: (ruleId: string, tagId: string) => void;
  onRuleTagRemove: (ruleId: string, tagId: string) => void;
  tagsUpdatingByRuleId: Map<string, boolean>;
  ManualAmountsSection: React.FC<{
    rule: RebateRule;
    manualAmountsByRuleId?: Map<string, ManualAmount[]>;
    manualAmountsLoadingProp?: boolean;
    handleOpenManualAmountModal: (
      rule: RebateRule,
      manualAmount?: ManualAmount,
    ) => void;
    handleDeleteManualAmount: (manualAmount: ManualAmount) => void;
    handleBulkDeleteManualAmounts?: (manualAmounts: ManualAmount[]) => void;
    isBulkDeleting?: boolean;
  }>;
  manualAmountsByRuleId?: Map<string, ManualAmount[]>;
  handleOpenManualAmountModal: (
    rule: RebateRule,
    manualAmount?: ManualAmount,
  ) => void;
  handleDeleteManualAmount: (manualAmount: ManualAmount) => void;
  handleBulkDeleteManualAmounts?: (manualAmounts: ManualAmount[]) => void;
  isBulkDeleting?: boolean;
  rebateTotalsByRule?: Map<string, number>;
  tierStatusByRule?: Map<
    string,
    { estimates?: number; predictions?: number; reported?: number }
  >;
  volumesByRule?: Map<
    string,
    {
      qualifyingVolume: number;
      affectedVolume: number;
      discountedQualifyingVolume: number;
      discountedAffectedVolume: number;
      discountPercentage: number;
    }
  >;
  agreementStartDate?: string;
  agreementEndDate?: string;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="cursor-grab active:cursor-grabbing mb-6"
    >
      <RuleCardDisplay
        rule={rule}
        isDragging={isDragging}
        isExpanded={expandedRules.has(rule.id)}
        onToggleExpansion={() => onToggleRuleExpansion(rule.id)}
        divisions={divisions}
        agreementStatus={agreementStatus}
        validationErrors={
          ruleValidationData.get(rule.id)?.validationErrors ?? []
        }
        areProductGroupsSame={areProductGroupsSame}
        tagsByRuleId={tagsByRuleId}
        availableTags={availableTags}
        tagsLoading={tagsLoadingProp}
        onRuleTagAdd={onRuleTagAdd}
        onRuleTagRemove={onRuleTagRemove}
        tagsUpdating={tagsUpdatingByRuleId.get(rule.id) || false}
        ManualAmountsSection={({ rule }) => (
          <ManualAmountsSection
            rule={rule}
            manualAmountsByRuleId={manualAmountsByRuleId}
            manualAmountsLoadingProp={false}
            handleOpenManualAmountModal={handleOpenManualAmountModal}
            handleDeleteManualAmount={handleDeleteManualAmount}
            handleBulkDeleteManualAmounts={handleBulkDeleteManualAmounts}
            isBulkDeleting={isBulkDeleting}
          />
        )}
        manualAmountsByRuleId={manualAmountsByRuleId}
        rebateTotal={rebateTotalsByRule?.get(rule.id)}
        tierStatus={tierStatusByRule?.get(rule.id)}
        volume={volumesByRule?.get(rule.id)}
        agreementStartDate={agreementStartDate}
        agreementEndDate={agreementEndDate}
      />
    </div>
  );
};

const ManualAmountsSection = ({
  rule,
  manualAmountsByRuleId,
  manualAmountsLoadingProp,
  handleOpenManualAmountModal,
  handleDeleteManualAmount,
  handleBulkDeleteManualAmounts,
  isBulkDeleting,
}: {
  rule: RebateRule;
  manualAmountsByRuleId?: Map<string, ManualAmount[]>;
  manualAmountsLoadingProp?: boolean;
  handleOpenManualAmountModal: (
    rule: RebateRule,
    manualAmount?: ManualAmount,
  ) => void;
  handleDeleteManualAmount: (manualAmount: ManualAmount) => void;
  handleBulkDeleteManualAmounts?: (manualAmounts: ManualAmount[]) => void;
  isBulkDeleting?: boolean;
}) => {
  const manualAmounts = manualAmountsByRuleId?.get(rule.id) || [];
  const isLoading = manualAmountsLoadingProp || false;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Reset selection when manual amounts change (e.g., after delete)
  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(manualAmounts.map((a) => a.id));
      const newSelected = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) {
          newSelected.add(id);
        }
      });
      return newSelected;
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [manualAmounts]);

  const handleSelectAll = useCallback(
    (checked: boolean | 'indeterminate') => {
      if (checked === true) {
        setSelectedIds(new Set(manualAmounts.map((a) => a.id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    // oxlint-disable-next-line exhaustive-deps
    [manualAmounts],
  );

  const handleSelectOne = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (handleBulkDeleteManualAmounts && selectedIds.size > 0) {
      const selectedAmounts = manualAmounts.filter((a) =>
        selectedIds.has(a.id),
      );
      handleBulkDeleteManualAmounts(selectedAmounts);
      setShowBulkDeleteConfirm(false);
      setSelectedIds(new Set());
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [handleBulkDeleteManualAmounts, selectedIds, manualAmounts]);

  const isAllSelected =
    manualAmounts.length > 0 && selectedIds.size === manualAmounts.length;
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <CircleNotchIcon className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const total =
    manualAmounts?.reduce(
      (sum, amount) => sum + parseFloat(amount.amount),
      0,
    ) || 0;

  const selectedTotal = manualAmounts
    .filter((a) => selectedIds.has(a.id))
    .reduce((sum, amount) => sum + parseFloat(amount.amount), 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-sm font-semibold">
          Manual Amounts ({manualAmounts?.length || 0})
        </h4>
        <div className="flex items-center gap-4">
          {selectedIds.size > 0 && (
            <Button
              onClick={() => setShowBulkDeleteConfirm(true)}
              variant="outline"
              size="sm"
              className="gap-2 text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
              disabled={isBulkDeleting}
            >
              {isBulkDeleting ? (
                <CircleNotchIcon className="h-4 w-4 animate-spin" />
              ) : (
                <TrashIcon className="h-4 w-4" />
              )}
              Delete Selected ({selectedIds.size})
            </Button>
          )}
          <span className="text-sm font-semibold text-gray-900">
            Total: $
            {total.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <Button
            onClick={() => handleOpenManualAmountModal(rule)}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add Amount
          </Button>
        </div>
      </div>

      {manualAmounts && manualAmounts.length > 0 ? (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      isAllSelected
                        ? true
                        : isSomeSelected
                          ? 'indeterminate'
                          : false
                    }
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                  />
                </TableHead>
                <TableHead className="font-semibold">Division</TableHead>
                <TableHead className="font-semibold">Amount</TableHead>
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {manualAmounts.map((amount) => (
                <TableRow
                  key={amount.id}
                  className={selectedIds.has(amount.id) ? 'bg-blue-50' : ''}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(amount.id)}
                      onCheckedChange={(checked) =>
                        handleSelectOne(amount.id, !!checked)
                      }
                      aria-label={`Select ${amount.divisionName}`}
                    />
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{amount.divisionName}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium text-gray-900">
                      $
                      {parseFloat(amount.amount).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {amount.date
                        ? parseLocalDate(
                            String(amount.date),
                          ).toLocaleDateString('en-US')
                        : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{amount.description}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleOpenManualAmountModal(rule, amount)
                              }
                              className="h-8 w-8 p-0"
                            >
                              <PencilSimpleIcon className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Edit</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteManualAmount(amount)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Delete</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground">
            No manual amounts recorded
          </p>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkDelete}
        title="Delete Selected Manual Amounts"
        message={`Are you sure you want to delete ${selectedIds.size} manual amount${selectedIds.size !== 1 ? 's' : ''} totaling $${selectedTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}? This action cannot be undone.`}
        confirmText="Delete"
        loading={isBulkDeleting}
        dangerous
      />
    </div>
  );
};
interface RebateRulesTabProps {
  // Data props
  agreementId: string;
  rebateRules: RebateRule[] | undefined;
  rebateRulesLoading: boolean;
  divisions: DivisionSimpleDTO[];
  agreementStatus: 'draft' | 'finalized';
  productGroups?: ProductGroup[];

  // Edit mode state (managed by container)
  isEditMode: boolean;
  isDirty: boolean;
  hasValidationErrors: boolean;
  isSaving: boolean;

  // Expansion state
  expandedRules: Set<string>;
  onToggleRuleExpansion: (ruleId: string) => void;

  // Edit mode actions
  onEnterEditMode: () => void;
  onCancelEditMode: () => void;
  onDiscardChanges: () => void;
  onSaveChanges: () => void;

  // Rule operations
  onCreateRule?: () => void;
  onUpdateRule?: (ruleId: string, updates: RebateRule) => void;
  onDeleteRule?: (rule: RebateRule) => void;
  onDuplicateRule?: (rule: RebateRule) => void;
  onValidationChange?: (ruleId: string, isValid: boolean) => void;
  onReorderRules?: (newOrder: RebateRule[]) => void;

  // Validation data (computed by container)
  ruleValidationStatus: Map<string, boolean>;
  getRuleValidationErrors: (rule: RebateRule) => string[];

  // For tracking deleted rules in edit mode
  deletedRuleIds?: Set<string>;
  originalRules?: RebateRule[];

  // Tags data (bulk fetched from container)
  tagsByRuleId: Map<string, CoreEnumOptionAssociation[]>;
  availableTags: CoreEnumOptionDTO[];
  tagsLoading: boolean;
  onRuleTagAdd: (ruleId: string, tagId: string) => void;
  onRuleTagRemove: (ruleId: string, tagId: string) => void;
  tagsUpdatingByRuleId: Map<string, boolean>;

  // Manual amounts data (bulk fetched)
  manualAmountsByRuleId?: Map<string, ManualAmount[]>;
  manualAmountsLoading?: boolean;

  // Rebate totals per rule
  rebateTotalsByRule?: Map<string, number>;

  // Tier status per rule by type
  tierStatusByRule?: Map<
    string,
    { estimates?: number; predictions?: number; reported?: number }
  >;
  // Volume totals per rule (gross and discounted)
  volumesByRule?: Map<
    string,
    {
      qualifyingVolume: number;
      affectedVolume: number;
      discountedQualifyingVolume: number;
      discountedAffectedVolume: number;
      discountPercentage: number;
    }
  >;

  // Agreement dates for rule duration context
  agreementStartDate?: string;
  agreementEndDate?: string;
  /** Line items for proportional split in manual amounts */
  lineItems?: RebateLineItem[];
}

export const RebateRulesTab: React.FC<RebateRulesTabProps> = ({
  // Data props
  agreementId,
  rebateRules,
  rebateRulesLoading,
  divisions,
  agreementStatus,
  productGroups = [],
  // Edit mode state
  isEditMode,
  isDirty,
  hasValidationErrors,
  isSaving,
  // Expansion
  expandedRules,
  onToggleRuleExpansion,
  // Edit mode actions
  onEnterEditMode,
  onCancelEditMode,
  onDiscardChanges,
  onSaveChanges,
  // Rule operations
  onCreateRule,
  onUpdateRule,
  onDeleteRule,
  onDuplicateRule,
  onValidationChange: onRuleValidationChange,
  onReorderRules,
  // Validation
  ruleValidationStatus,
  getRuleValidationErrors,
  // Deleted rules tracking
  deletedRuleIds,
  originalRules,
  // Tags data (bulk fetched)
  tagsByRuleId = new Map(),
  availableTags = [],
  tagsLoading: tagsLoadingProp = false,
  onRuleTagAdd,
  onRuleTagRemove,
  tagsUpdatingByRuleId = new Map(),
  // Manual amounts data (bulk fetched)
  manualAmountsByRuleId,
  manualAmountsLoading: manualAmountsLoadingProp,
  // Rebate totals per rule
  rebateTotalsByRule,
  // Tier status per rule by type
  tierStatusByRule,
  // Volume totals per rule
  volumesByRule,
  // Agreement dates
  agreementStartDate,
  agreementEndDate,
  // Line items for proportional split
  lineItems,
}) => {
  const { showToast } = useToast();

  // Compute display rules - includes deleted rules in edit mode for visual feedback
  const displayRules = useMemo(() => {
    if (!isEditMode) return rebateRules || [];

    if (!originalRules) return rebateRules || [];

    // Keep original order and show deleted rules in place
    const merged = originalRules.map((orig) => {
      if (deletedRuleIds?.has(orig.id)) {
        return { ...orig, _isDeleted: true }; // optional flag for styling
      }
      const updated = rebateRules?.find((r) => r.id === orig.id);
      return updated || orig;
    });

    // Add any new rules (ones not present in originalRules)
    const newRules =
      rebateRules?.filter((r) => !originalRules.find((o) => o.id === r.id)) ??
      [];

    return [...merged, ...newRules];
  }, [isEditMode, rebateRules, originalRules, deletedRuleIds]);

  // Unified validation logic for each rule (using data from container)
  const ruleValidationData = useMemo(() => {
    const validationMap = new Map<
      string,
      { hasErrors: boolean; validationErrors: string[] }
    >();

    displayRules?.forEach((rule) => {
      const businessRuleErrors = getRuleValidationErrors(rule);
      const formValidationStatus = ruleValidationStatus?.get(rule.id);

      // Unified logic: form validation takes precedence, but always check business rules
      const hasErrors =
        formValidationStatus === false || businessRuleErrors.length > 0;

      validationMap.set(rule.id, {
        hasErrors,
        validationErrors: businessRuleErrors,
      });
    });

    return validationMap;
  }, [displayRules, ruleValidationStatus, getRuleValidationErrors]);

  // Compute rule changes for visual feedback
  const computedRuleChanges = useMemo(() => {
    if (!isEditMode || !originalRules) {
      return new Map<string, ChangeStatus>();
    }

    const changes = new Map<string, ChangeStatus>();
    const currentRules = rebateRules || [];

    // Check for removed and modified rules
    originalRules.forEach((orig) => {
      if (deletedRuleIds?.has(orig.id)) {
        changes.set(orig.id, 'removed');
      } else {
        const current = currentRules.find((r) => r.id === orig.id);
        if (!current) {
          changes.set(orig.id, 'removed');
        } else if (JSON.stringify(orig) !== JSON.stringify(current)) {
          changes.set(orig.id, 'modified');
        } else {
          changes.set(orig.id, 'unchanged');
        }
      }
    });

    // Check for added rules
    currentRules.forEach((current) => {
      if (
        !originalRules.find((r) => r.id === current.id) &&
        !deletedRuleIds?.has(current.id)
      ) {
        changes.set(current.id, 'added');
      }
    });

    return changes;
  }, [isEditMode, rebateRules, originalRules, deletedRuleIds]);

  // State for ordered rules
  const [orderedRules, setOrderedRules] = useState<RebateRule[]>(
    displayRules || [],
  );

  // No need to separate active rules - drag-and-drop is disabled in edit mode

  // Update ordered rules when displayRules changes
  useEffect(() => {
    if (!displayRules) return;

    const sorted = [...displayRules];
    if (!isEditMode) {
      sorted.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    }

    setOrderedRules(sorted);
  }, [displayRules, isEditMode]);

  // Drag and drop sensors
  // Only use PointerSensor - disable keyboard drag to prevent interference with input fields
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before starting drag
      },
    }),
  );

  const [manualAmountModal, setManualAmountModal] = useState<{
    open: boolean;
    rule: RebateRule | null;
    manualAmount: ManualAmount | null;
  }>({
    open: false,
    rule: null,
    manualAmount: null,
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    manualAmount: ManualAmount | null;
  }>({
    open: false,
    manualAmount: null,
  });

  const deleteManualAmountMutation = useDeleteManualAmount(
    deleteConfirmation.manualAmount?.id || '',
  );

  // Reorder mutation
  const reorderMutation = useReorderRebateRules(agreementId);

  // Handle drag end
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      // Disable drag-and-drop in edit mode to avoid complexity with deleted rules
      if (isEditMode) return;

      if (over && active.id !== over.id) {
        const oldIndex = orderedRules.findIndex(
          (rule) => rule.id === active.id,
        );
        const newIndex = orderedRules.findIndex((rule) => rule.id === over.id);

        const newOrderedRules = arrayMove(orderedRules, oldIndex, newIndex);
        setOrderedRules(newOrderedRules);

        try {
          const ruleOrders = newOrderedRules.map((rule, index) => ({
            id: rule.id,
            sortOrder: index,
          }));

          await reorderMutation.mutateAsync({ ruleOrders });

          if (onReorderRules) onReorderRules(newOrderedRules);
        } catch (error) {
          setOrderedRules(orderedRules);
          showToast({
            message: 'Failed to save rule order. Please try again.',
            title: 'Error',
            color: 'error',
          });
        }
      }
    },
    [isEditMode, orderedRules, reorderMutation, onReorderRules, showToast],
  );

  const handleOpenManualAmountModal = (
    rule: RebateRule,
    manualAmount?: ManualAmount,
  ) => {
    setManualAmountModal({
      open: true,
      rule,
      manualAmount: manualAmount || null,
    });
  };

  const handleCloseManualAmountModal = useCallback(() => {
    setManualAmountModal({
      open: false,
      rule: null,
      manualAmount: null,
    });
  }, []);

  const handleDeleteManualAmount = (manualAmount: ManualAmount) => {
    setDeleteConfirmation({
      open: true,
      manualAmount,
    });
  };

  const handleConfirmDeleteManualAmount = useCallback(async () => {
    if (!deleteConfirmation.manualAmount) return;

    try {
      await deleteManualAmountMutation.mutateAsync({});
      setDeleteConfirmation({ open: false, manualAmount: null });
      showToast({
        message: 'Manual amount deleted successfully',
        title: 'Success',
        color: 'success',
      });
    } catch (error) {
      showToast({
        message: 'Failed to delete manual amount',
        title: 'Error',
        color: 'error',
      });
    }
  }, [deleteConfirmation.manualAmount, deleteManualAmountMutation, showToast]);

  // Bulk delete hook
  const {
    bulkDelete: handleBulkDeleteManualAmounts,
    isLoading: isBulkDeleting,
  } = useBulkDeleteManualAmounts();

  // Helper function to check if qualifying and affected groups are the same
  const areProductGroupsSame = (rule: RebateRule): boolean => {
    // For manual rules, inclusion types might be null, so we can't compare them
    if (rule.isManual) {
      return false;
    }

    // Check if inclusion types are the same
    if (rule.qualifyingInclusionType !== rule.affectedInclusionType) {
      return false;
    }

    // Check if product groups are the same
    if (
      rule.qualifyingProductGroups.length !== rule.affectedProductGroups.length
    ) {
      return false;
    }

    const qualifyingIds = rule.qualifyingProductGroups.map((g) => g.id).sort();
    const affectedIds = rule.affectedProductGroups.map((g) => g.id).sort();

    return qualifyingIds.every((id, index) => id === affectedIds[index]);
  };

  // Memoized edit mode control buttons
  const EditModeControlButtons = useMemo(() => {
    if (!isEditMode) {
      // Show "Edit Rules" button when not in edit mode
      return (
        <div className="mb-4">
          <Button variant="default" onClick={onEnterEditMode} className="gap-2">
            <PencilSimpleIcon className="h-4 w-4" />
            Edit Rules
          </Button>
        </div>
      );
    }

    // Show Save/Cancel/Discard buttons in edit mode
    return (
      <div className="mb-4 flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onCancelEditMode}>
            <XIcon className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={onDiscardChanges}
            disabled={!isDirty}
          >
            <ArrowCounterClockwiseIcon className="h-4 w-4 mr-2" />
            Discard Changes
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="default"
                    onClick={onSaveChanges}
                    disabled={!isDirty || hasValidationErrors || isSaving}
                  >
                    {isSaving ? (
                      <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FloppyDiskIcon className="h-4 w-4 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </span>
              </TooltipTrigger>
              {hasValidationErrors && (
                <TooltipContent>
                  <p>Please fix validation errors in rules before saving</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex-1 flex items-center gap-2">
          <WrenchIcon className="h-5 w-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">
            {isDirty ? 'You have unsaved changes' : 'Edit mode'}
          </span>
        </div>
      </div>
    );
  }, [
    isEditMode,
    isDirty,
    hasValidationErrors,
    isSaving,
    onEnterEditMode,
    onCancelEditMode,
    onDiscardChanges,
    onSaveChanges,
  ]);

  // Memoized Add Rule button (card-sized, at bottom of list)
  const AddRuleButton = useMemo(() => {
    // Show in edit mode OR when agreement is in draft status
    if (!onCreateRule) return null;

    return (
      <Card className="border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50/30 transition-colors cursor-pointer">
        <button
          onClick={onCreateRule}
          className="w-full p-6 flex items-center justify-center gap-3 text-gray-600 hover:text-blue-600"
        >
          <PlusIcon className="h-5 w-5" />
          <span className="text-base font-medium">Add New Rule</span>
        </button>
      </Card>
    );
  }, [onCreateRule]);

  // Memoized edit mode indicator
  const EditModeIndicator = useMemo(() => {
    if (!isEditMode) return null;

    return (
      <div className="mb-4 rounded-md bg-blue-100 border border-blue-300 p-3 text-sm text-blue-900">
        <div className="flex items-center gap-2">
          <span className="font-semibold">✏️ Edit Mode Active</span>
          <span className="text-blue-700">
            - Changes will be saved when you click &quot;Save Changes&quot; •
            Rule reordering is disabled during edit mode
          </span>
        </div>
      </div>
    );
  }, [isEditMode]);

  // Memoized changes preview banner
  const ChangesPreviewBanner = useMemo(() => {
    if (!isEditMode) return null;

    const hasChanges = Array.from(computedRuleChanges.values()).some(
      (status) => status !== 'unchanged',
    );

    return (
      <UICard className="mb-4 p-4 border-slate-200 bg-slate-50">
        <div className="flex items-start gap-3">
          <WarningCircleIcon className="h-5 w-5 text-slate-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900 mb-3">
              Rules Overview
              {hasChanges && (
                <span className="ml-2 text-xs font-normal text-slate-600">
                  (
                  {
                    Array.from(computedRuleChanges.values()).filter(
                      (s) => s !== 'unchanged',
                    ).length
                  }{' '}
                  change
                  {Array.from(computedRuleChanges.values()).filter(
                    (s) => s !== 'unchanged',
                  ).length !== 1
                    ? 's'
                    : ''}
                  )
                </span>
              )}
            </div>
            <div className="space-y-1.5 text-sm">
              {Array.from(computedRuleChanges.entries()).map(
                ([ruleId, status]) => {
                  // Look in rebateRules for the rule information
                  const rule =
                    rebateRules?.find((r) => r.id === ruleId) ||
                    originalRules?.find((r) => r.id === ruleId);
                  if (!rule) return null;

                  const statusConfig = getStatusStyle(status);
                  const label = getStatusLabel(status);

                  return (
                    <div
                      key={ruleId}
                      className={`flex items-center justify-between gap-3 p-2.5 rounded-md border ${statusConfig.bg} ${statusConfig.border}`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {statusConfig.icon && (
                          <div
                            className={`flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 ${statusConfig.iconBg}`}
                          >
                            {statusConfig.icon}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div
                            className={`font-medium ${statusConfig.textColor}`}
                          >
                            {rule.name}
                          </div>
                        </div>
                      </div>
                      <div
                        className={`text-xs font-semibold px-2 py-1 rounded ${statusConfig.textColor}`}
                      >
                        {label}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </div>
        </div>
      </UICard>
    );
  }, [isEditMode, computedRuleChanges, rebateRules, originalRules]);

  // Memoized warning banner
  const WarningBanner = useMemo(() => {
    if (
      rebateRulesLoading ||
      (orderedRules && orderedRules.length > 0) ||
      agreementStatus !== 'draft'
    ) {
      return null;
    }

    return (
      <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-orange-600"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-orange-800">
              Add at least one rebate rule to finalize this agreement
            </h3>
            <p className="mt-1 text-sm text-orange-700">
              Agreements must have rebate rules defined before they can be
              finalized and used for processing invoices.
            </p>
          </div>
        </div>
      </div>
    );
  }, [rebateRulesLoading, orderedRules, agreementStatus]);

  // Memoized rules content
  const RulesContent = useMemo(() => {
    if (rebateRulesLoading) {
      return (
        <div className="flex justify-center p-6">
          <CircleNotchIcon className="h-6 w-6 animate-spin" />
        </div>
      );
    }

    if (!orderedRules || orderedRules.length === 0) {
      return (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">
            No rebate rules defined for this agreement
          </p>
        </div>
      );
    }

    if (isEditMode) {
      // Edit mode: Show editable cards (no drag, inline edit forms)
      return (
        <div className="space-y-6">
          {orderedRules.map((rule) => (
            <RuleCardEditable
              key={rule.id}
              rule={rule}
              isExpanded={expandedRules.has(rule.id)}
              onToggleExpansion={() => onToggleRuleExpansion(rule.id)}
              onDelete={() => onDeleteRule?.(rule)}
              onDuplicate={() => onDuplicateRule?.(rule)}
              onUpdate={onUpdateRule}
              onValidationChange={onRuleValidationChange}
              isValid={ruleValidationStatus?.get(rule.id)}
              divisions={divisions}
              productGroups={productGroups}
              agreementStatus={agreementStatus}
              hasErrors={ruleValidationData.get(rule.id)?.hasErrors ?? false}
              validationErrors={
                ruleValidationData.get(rule.id)?.validationErrors ?? []
              }
              changeStatus={computedRuleChanges.get(rule.id)}
              manualAmountsByRuleId={manualAmountsByRuleId}
              rebateTotal={rebateTotalsByRule?.get(rule.id)}
              tierStatus={tierStatusByRule?.get(rule.id)}
              agreementStartDate={agreementStartDate}
              agreementEndDate={agreementEndDate}
            />
          ))}
        </div>
      );
    } else {
      // Normal mode: Enable drag-and-drop with display cards
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedRules.map((rule) => rule.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-6">
              {orderedRules.map((rule) => (
                <SortableRuleCard
                  key={rule.id}
                  rule={rule}
                  expandedRules={expandedRules}
                  onToggleRuleExpansion={onToggleRuleExpansion}
                  divisions={divisions}
                  agreementStatus={agreementStatus}
                  ruleValidationData={ruleValidationData}
                  areProductGroupsSame={areProductGroupsSame}
                  tagsByRuleId={tagsByRuleId}
                  availableTags={availableTags}
                  tagsLoadingProp={tagsLoadingProp}
                  onRuleTagAdd={onRuleTagAdd}
                  onRuleTagRemove={onRuleTagRemove}
                  tagsUpdatingByRuleId={tagsUpdatingByRuleId}
                  manualAmountsByRuleId={manualAmountsByRuleId}
                  ManualAmountsSection={ManualAmountsSection}
                  handleOpenManualAmountModal={handleOpenManualAmountModal}
                  handleDeleteManualAmount={handleDeleteManualAmount}
                  handleBulkDeleteManualAmounts={handleBulkDeleteManualAmounts}
                  isBulkDeleting={isBulkDeleting}
                  rebateTotalsByRule={rebateTotalsByRule}
                  tierStatusByRule={tierStatusByRule}
                  volumesByRule={volumesByRule}
                  agreementStartDate={agreementStartDate}
                  agreementEndDate={agreementEndDate}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      );
    }
  }, [
    rebateRulesLoading,
    orderedRules,
    isEditMode,
    expandedRules,
    onUpdateRule,
    onRuleValidationChange,
    ruleValidationStatus,
    divisions,
    productGroups,
    agreementStatus,
    ruleValidationData,
    computedRuleChanges,
    manualAmountsByRuleId,
    rebateTotalsByRule,
    tierStatusByRule,
    onToggleRuleExpansion,
    onDeleteRule,
    onDuplicateRule,
    sensors,
    handleDragEnd,
    tagsByRuleId,
    availableTags,
    tagsLoadingProp,
    onRuleTagAdd,
    onRuleTagRemove,
    tagsUpdatingByRuleId,
    volumesByRule,
    agreementStartDate,
    agreementEndDate,
    handleBulkDeleteManualAmounts,
    isBulkDeleting,
  ]);

  // Memoized modals
  const Modals = useMemo(
    () => (
      <>
        {/* Manual Amount Modal */}
        {manualAmountModal.open && manualAmountModal.rule && (
          <ManualAmountModal
            open={manualAmountModal.open}
            onClose={handleCloseManualAmountModal}
            rule={manualAmountModal.rule}
            divisions={divisions}
            manualAmount={manualAmountModal.manualAmount}
            onSuccess={handleCloseManualAmountModal}
            agreementStartDate={agreementStartDate}
            agreementEndDate={agreementEndDate}
            lineItems={lineItems}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteConfirmation.open}
          onClose={() =>
            setDeleteConfirmation({ open: false, manualAmount: null })
          }
          onConfirm={handleConfirmDeleteManualAmount}
          title="Delete Manual Amount"
          message={`Are you sure you want to delete this manual amount of $${
            deleteConfirmation.manualAmount
              ? parseFloat(
                  deleteConfirmation.manualAmount.amount,
                ).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : '0.00'
          }?`}
          confirmText="Delete"
          loading={deleteManualAmountMutation.isLoading}
        />
      </>
    ),
    [
      manualAmountModal,
      deleteConfirmation,
      divisions,
      handleCloseManualAmountModal,
      handleConfirmDeleteManualAmount,
      deleteManualAmountMutation.isLoading,
      agreementStartDate,
      agreementEndDate,
      lineItems,
    ],
  );

  return (
    <div className="space-y-4">
      {EditModeControlButtons}
      <div
        className={`space-y-4 ${
          isEditMode
            ? 'rounded-lg border-2 border-blue-400 bg-blue-50/30 p-4 shadow-sm'
            : ''
        }`}
      >
        {EditModeIndicator}
        {ChangesPreviewBanner}
        {WarningBanner}
        {RulesContent}
        {AddRuleButton}
        {Modals}
      </div>
    </div>
  );
};

export default RebateRulesTab;
